import { Box, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import pageUrls from 'constants/pageUrls';

import Button from 'components/Button';
import DeviceList from 'components/DeviceComponents/DeviceList';
import GroupNames from 'components/DeviceComponents/GroupNames';
import RightComponentWrapper from 'components/DeviceComponents/RightComponentWrapper';
import ErrorText from 'components/ErrorText';
import OrganizationsIcon from 'components/Icons/OrganizationsIcon/intex';
import SuccessIcon from 'components/Icons/SuccessIcon';
import Modal from 'components/Modal';

import { useAppDispatch, useAppSelector } from 'redux-store';
import type { DeviceSerial } from 'redux-store/slices/ui/types';
import deleteDeviceThunk from 'redux-store/thunks/devices/deleteDeviceThunk';
import getDeviceOrganisationsThunk from 'redux-store/thunks/devices/getDeviceOrganisationsThunk';

interface Props extends DefaultPopupProps {
  serial: DeviceSerial | null;
}

const RemoveDeviceModal: FC<Props> = ({ onClose, serial, open }) => {
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const { t } = useTranslation();

  const deviceOrganizations = useAppSelector(s => s.deviceOrganizations.items);

  const removeDevice = useCallback(async () => {
    if (!serial) return;

    try {
      setLoading(true);
      const result = await dispatch(deleteDeviceThunk(serial)).unwrap();

      if (!result.success) {
        setError(t('common:pages.settings.remove_device_error'));
      } else {
        setSuccess(true);
        setError(null);
        setTimeout(async () => {
          await router.push(pageUrls.devices.all);
          onClose();
          setSuccess(false);
        }, 1000);
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, onClose, router, serial, t]);

  useEffect(() => {
    if (serial) {
      dispatch(getDeviceOrganisationsThunk({ serials: serial }));
    }
  }, [dispatch, serial]);

  return (
    <Modal open={open} onClose={onClose} hideCloseButton={success}>
      {!success ? (
        <Box height="100%" sx={{ overflowY: 'auto' }}>
          <Box mb={4}>
            <Typography
              variant="h2"
              textAlign="center"
              data-testid="delete-device-modal"
            >
              {t('common:pages.settings.deleteDevice')}
            </Typography>
          </Box>
          <Box mb={4}>
            <Typography variant="body1" textAlign="center">
              {t(
                deviceOrganizations.length > 0
                  ? 'common:pages.settings.remove_device_description_2'
                  : 'common:pages.settings.remove_device_description',
              )}
            </Typography>
            {deviceOrganizations.length > 0 ? (
              <Box mt={4} mb={4} width="100%">
                {deviceOrganizations.map(item => (
                  <DeviceList
                    key={item.serial}
                    serial={item.serial}
                    rightComponent={
                      item.organizations.length > 0 ? (
                        <RightComponentWrapper>
                          <OrganizationsIcon
                            fontSize="small"
                            sx={{ color: 'grey.500' }}
                          />
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body1" noWrap color="grey.500">
                              <GroupNames items={item.organizations} />
                            </Typography>
                          </Box>
                        </RightComponentWrapper>
                      ) : null
                    }
                  />
                ))}
              </Box>
            ) : null}
          </Box>
          <Box display="flex" flexDirection="row" gap={1} width="100%">
            <Button
              onClick={onClose}
              color="neutral"
              fullWidth
              variant="flat"
              data-testid="cancel-button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              disabled={isLoading}
              onClick={removeDevice}
              fullWidth
              variant="flat"
              color="error"
              data-testid="submit"
            >
              {t('common:pages.settings.deleteDeviceButton')}
            </Button>
          </Box>
          <ErrorText error={error} mt={2} />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Box
            margin="50px 20px"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <SuccessIcon width={100} height={100} />
            <Typography
              variant="h1"
              margin="20px 10px"
              data-testid="success-text"
            >
              {t('common:pages.settings.device_removed')}
            </Typography>
          </Box>
        </Box>
      )}
    </Modal>
  );
};

export default RemoveDeviceModal;
